'use client'
import Link from 'next/link'
import { ArrowRight, BookOpen, Wrench, Sparkles, Lock } from 'lucide-react'

type BonusTone = 'amber' | 'rose' | 'emerald'

type BonusCard = {
  slug: string
  href: string
  eyebrow: string
  title: string
  blurb: string
  valueWas: string
  icon: React.ComponentType<{ className?: string }>
  tone: BonusTone
}

const CARDS: BonusCard[] = [
  {
    slug: 'idukki-prompt-pack',
    href: '/resources/idukki-prompt-pack',
    eyebrow: 'Bonus 01',
    title: 'The Idukki Prompt Pack',
    blurb: 'Ready-to-paste prompts for AI tagging, rights-request DMs, review summaries and agent-ready PDP copy. Twenty-three prompts, all field-tested.',
    valueWas: '£400 value',
    icon: BookOpen,
    tone: 'amber',
  },
  {
    slug: 'dtc-ai-toolkit',
    href: '/resources/dtc-ai-toolkit',
    eyebrow: 'Bonus 02',
    title: 'The DTC AI Toolkit',
    blurb: 'Every AI tool in the Idukki customer stack, named, with the workflow each fits. From AI captioning to agent-readiness audits.',
    valueWas: '£600 value',
    icon: Wrench,
    tone: 'rose',
  },
  {
    slug: 'hook-and-hero-shot-generator',
    href: '/resources/hook-and-hero-shot-generator',
    eyebrow: 'Bonus 03',
    title: 'Hook + Hero-Shot Generator',
    blurb: 'Twenty hook formulas for shoppable video, plus the thumbnail-crop framework that lifts CTR. Copy, paste, ship.',
    valueWas: '£300 value',
    icon: Sparkles,
    tone: 'emerald',
  },
]

const TONE_BG: Record<BonusTone, string> = {
  amber:   'from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30',
  rose:    'from-rose-50 to-pink-50 dark:from-rose-950/30 dark:to-pink-950/30',
  emerald: 'from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30',
}

const TONE_RING: Record<BonusTone, string> = {
  amber:   'ring-amber-200/60 dark:ring-amber-800/40',
  rose:    'ring-rose-200/60 dark:ring-rose-800/40',
  emerald: 'ring-emerald-200/60 dark:ring-emerald-800/40',
}

const TONE_ICON: Record<BonusTone, string> = {
  amber:   'bg-amber-500/10 text-amber-700 dark:text-amber-300',
  rose:    'bg-rose-500/10 text-rose-700 dark:text-rose-300',
  emerald: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
}

// Creative-zone ground: a deeper tinted wash so the code-built motif sits
// on its own "header image" plate above the white-ish card body.
const TONE_ZONE: Record<BonusTone, string> = {
  amber:   'from-amber-100/80 via-orange-50 to-amber-50 dark:from-amber-900/40 dark:via-orange-950/30 dark:to-amber-950/20',
  rose:    'from-rose-100/80 via-pink-50 to-rose-50 dark:from-rose-900/40 dark:via-pink-950/30 dark:to-rose-950/20',
  emerald: 'from-emerald-100/80 via-teal-50 to-emerald-50 dark:from-emerald-900/40 dark:via-teal-950/30 dark:to-emerald-950/20',
}

// Accent colours used inside the SVG/CSS motifs. Lime stays the shared
// Idukki signal; the tone colour gives each card its own identity.
const TONE_ACCENT: Record<BonusTone, { tint: string; soft: string }> = {
  amber:   { tint: 'text-amber-600 dark:text-amber-300',   soft: 'text-amber-400/70' },
  rose:    { tint: 'text-rose-600 dark:text-rose-300',     soft: 'text-rose-400/70' },
  emerald: { tint: 'text-emerald-600 dark:text-emerald-300', soft: 'text-emerald-400/70' },
}

const LIME = '#CDEB45'

// ── Per-card code-built "creative" header. Pure SVG + CSS, decorative
//    only (aria-hidden), themed to the bonus. Lime is the shared accent. ──

function PromptPackArt() {
  // Stacked prompt cards / book motif: three layered "prompt" sheets with
  // text-line stand-ins, the front one carrying a lime command chip.
  return (
    <div aria-hidden className="relative h-full w-full">
      <div className="absolute left-1/2 top-1/2 h-24 w-32 -translate-x-1/2 -translate-y-1/2">
        <div className="absolute inset-0 translate-x-3 translate-y-3 rotate-6 rounded-lg border border-amber-300/50 bg-white/60 dark:bg-white/10" />
        <div className="absolute inset-0 translate-x-1.5 translate-y-1.5 rotate-3 rounded-lg border border-amber-300/60 bg-white/75 dark:bg-white/[0.07]" />
        <div className="idk-bonus-breathe absolute inset-0 rounded-lg border border-amber-300/70 bg-white shadow-sm dark:bg-zinc-900">
          <div className="flex h-full flex-col gap-1.5 p-2.5">
            <span className="inline-flex w-fit items-center rounded px-1.5 py-0.5 text-[7px] font-bold uppercase tracking-wider text-zinc-900" style={{ backgroundColor: LIME }}>
              Prompt
            </span>
            <span className="h-1 w-full rounded-full bg-amber-300/60" />
            <span className="h-1 w-10/12 rounded-full bg-amber-200/70" />
            <span className="h-1 w-8/12 rounded-full bg-amber-200/60" />
          </div>
        </div>
      </div>
      <span className="idk-bonus-float absolute right-4 top-3 font-mono text-sm font-bold text-amber-500/70">{'{ }'}</span>
    </div>
  )
}

function ToolkitArt() {
  // Tool + node-graph motif: a connected node mesh (the "stack") with a
  // lime hub node, plus a floating wrench glyph.
  return (
    <div aria-hidden className="relative h-full w-full">
      <svg viewBox="0 0 160 96" className="absolute inset-0 h-full w-full">
        <g stroke="currentColor" className="text-rose-400/50" strokeWidth="1.5" fill="none">
          <line x1="80" y1="48" x2="36" y2="26" />
          <line x1="80" y1="48" x2="34" y2="70" />
          <line x1="80" y1="48" x2="124" y2="28" />
          <line x1="80" y1="48" x2="126" y2="68" />
        </g>
        <circle cx="36" cy="26" r="6" className="fill-rose-300/80" />
        <circle cx="34" cy="70" r="6" className="fill-rose-300/80" />
        <circle cx="124" cy="28" r="6" className="fill-rose-300/80" />
        <circle cx="126" cy="68" r="6" className="fill-rose-300/80" />
        <circle cx="80" cy="48" r="11" fill={LIME} stroke="white" strokeWidth="2" />
      </svg>
      <div className="idk-bonus-breathe absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <Wrench className="h-5 w-5 text-zinc-900/80" />
      </div>
      <span className="idk-bonus-float absolute left-5 top-4 h-2 w-2 rounded-full bg-rose-400/60" />
    </div>
  )
}

function HookArt() {
  // Sparkle + thumbnail-crop frame: a "hero shot" tile with corner crop
  // marks and a twinkling lime sparkle, implying CTR-tuned thumbnails.
  return (
    <div aria-hidden className="relative h-full w-full">
      <div className="absolute left-1/2 top-1/2 h-20 w-28 -translate-x-1/2 -translate-y-1/2">
        <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-emerald-200/80 to-teal-100/70 ring-1 ring-emerald-300/60 dark:from-emerald-800/40 dark:to-teal-900/30" />
        {/* crop corner marks */}
        {[
          'left-1 top-1 border-l-2 border-t-2',
          'right-1 top-1 border-r-2 border-t-2',
          'left-1 bottom-1 border-l-2 border-b-2',
          'right-1 bottom-1 border-r-2 border-b-2',
        ].map((pos) => (
          <span key={pos} className={`absolute h-3 w-3 border-emerald-500/80 ${pos}`} />
        ))}
        {/* play glyph implying a shoppable video hero shot */}
        <span className="absolute left-1/2 top-1/2 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 shadow-sm dark:bg-zinc-900/80">
          <span className="ml-0.5 h-0 w-0 border-y-[5px] border-l-[8px] border-y-transparent border-l-emerald-600" />
        </span>
      </div>
      <Sparkles className="idk-bonus-twinkle absolute right-4 top-3 h-4 w-4" style={{ color: LIME }} />
      <Sparkles className="idk-bonus-twinkle absolute left-5 bottom-3 h-3 w-3 text-emerald-500/70" style={{ animationDelay: '1.2s' }} />
    </div>
  )
}

function CreativeZone({ tone }: { tone: BonusTone }) {
  return (
    <div className={`relative -mx-6 -mt-6 mb-5 h-28 overflow-hidden bg-gradient-to-br ${TONE_ZONE[tone]} sm:-mx-7 sm:-mt-7`}>
      {/* dotted texture, echoing the navy hero pattern from CLAUDE.md cues */}
      <span
        aria-hidden
        className="absolute inset-0 opacity-[0.5]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
          backgroundSize: '11px 11px',
          color: 'rgba(0,0,0,0.06)',
        }}
      />
      {tone === 'amber' && <PromptPackArt />}
      {tone === 'rose' && <ToolkitArt />}
      {tone === 'emerald' && <HookArt />}
      {/* hover sheen sweep */}
      <span aria-hidden className="idk-bonus-sheen pointer-events-none absolute inset-0" />
    </div>
  )
}

export function BonusResourcesBand() {
  return (
    <section className="container-wide py-12 md:py-20">
      <div className="mb-8 flex flex-col items-start gap-3 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-primary">Free resources, no gate</p>
          <h2 className="mt-2 font-display text-3xl font-medium tracking-tight md:text-4xl">
            Three bonuses worth <span className="text-primary">£1,300</span>. Yours, no email required.
          </h2>
          <p className="mt-3 text-sm text-muted-foreground md:text-base">
            We keep these pages public on purpose. If they help you ship better UGC without ever talking to us, that is a win we are happy to underwrite.
          </p>
        </div>
      </div>

      {/* Mobile: horizontal snap-scroll carousel (cards ~85% wide so
          the next peeks ~15%). md+ restores the original 3-up grid. */}
      <ul className="no-scrollbar -mx-4 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-px-4 px-4 pb-2 md:mx-0 md:grid md:grid-cols-3 md:gap-6 md:overflow-visible md:px-0 md:pb-0">
        {CARDS.map((c) => {
          const Icon = c.icon
          const accent = TONE_ACCENT[c.tone]
          return (
            <li key={c.slug} className="flex w-[85%] shrink-0 snap-start md:w-auto md:shrink">
              <Link
                href={c.href}
                className={`group relative flex w-full flex-col overflow-hidden rounded-2xl bg-gradient-to-br ${TONE_BG[c.tone]} ring-1 ${TONE_RING[c.tone]} p-6 transition duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/5 sm:p-7`}
              >
                {/* Code-built creative header zone, themed per bonus. */}
                <CreativeZone tone={c.tone} />

                {/* Storytelling hierarchy: icon + kicker, then title, blurb,
                    value row. */}
                <div className="flex items-center justify-between">
                  <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${TONE_ICON[c.tone]} transition group-hover:scale-110`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className={`text-[10px] font-semibold uppercase tracking-widest ${accent.tint}`}>{c.eyebrow}</span>
                </div>

                <h3 className="mt-4 font-display text-xl font-semibold leading-snug text-foreground sm:text-2xl">
                  {c.title}
                </h3>

                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {c.blurb}
                </p>

                <div className="mt-6 flex items-center justify-between border-t border-foreground/5 pt-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs text-muted-foreground line-through">{c.valueWas}</span>
                    <span className="text-sm font-bold uppercase tracking-wider text-primary">Free</span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-foreground/80 group-hover:text-primary">
                    Open <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            </li>
          )
        })}
      </ul>

      <p className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
        <Lock className="h-3 w-3" />
        No lead-capture, no Calendly, no upsell modal. Click, read, ship.
      </p>
    </section>
  )
}
